import { useState, useEffect } from "react"
import styled from "styled-components"
import ReactMarkdown from "react-markdown"
import MdEditor from "react-markdown-editor-lite"
import MarkdownIt from "markdown-it"
import "react-markdown-editor-lite/lib/index.css"

import colors from "~constants/colors"

// Same parser config the wizard's MarkdownEditor uses, so the inline editor's
// live preview matches what authors see when creating a perk.
const mdParser = new MarkdownIt({ html: true })

export type EditableFieldType = "text" | "textarea" | "markdown"

interface EditableFieldProps {
  label: string
  value?: string
  /** Controls the editor used and how the read value is rendered. */
  type?: EditableFieldType
  placeholder?: string
  /** When false the field is read-only (no pencil). */
  canEdit?: boolean
  /**
   * Persists the new value. Should resolve on success and reject on failure
   * (the parent is responsible for surfacing the error toast). On reject the
   * field stays in edit mode so the user does not lose their input.
   */
  onSave: (value: string) => Promise<void>
}

/* A tiny inline pencil so we don't need to add an icon asset. */
const PencilIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
  </svg>
)

const EditableField = ({
  label,
  value,
  type = "text",
  placeholder,
  canEdit = false,
  onSave,
}: EditableFieldProps) => {
  const [isEditing, setIsEditing] = useState(false)
  const [draft, setDraft] = useState(value ?? "")
  const [isSaving, setIsSaving] = useState(false)

  // Keep the draft in sync when the underlying value changes while not editing
  // (e.g. another field is saved and the parent re-renders with fresh data).
  useEffect(() => {
    if (!isEditing) setDraft(value ?? "")
  }, [value, isEditing])

  const startEditing = () => {
    setDraft(value ?? "")
    setIsEditing(true)
  }

  const cancelEditing = () => {
    setDraft(value ?? "")
    setIsEditing(false)
  }

  const handleSave = async () => {
    const trimmed = draft.trim()
    // Nothing changed — just close the editor.
    if (trimmed === (value ?? "").trim()) {
      setIsEditing(false)
      return
    }

    setIsSaving(true)
    try {
      await onSave(trimmed)
      setIsEditing(false)
    } catch {
      // Parent surfaces the error; stay in edit mode so input isn't lost.
    } finally {
      setIsSaving(false)
    }
  }

  // Cmd/Ctrl+Enter saves; Escape cancels. Plain Enter saves single-line text.
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      e.preventDefault()
      cancelEditing()
      return
    }
    if (e.key === "Enter" && (type === "text" || e.metaKey || e.ctrlKey)) {
      e.preventDefault()
      handleSave()
    }
  }

  const renderReadValue = () => {
    if (!value) return <Empty>--</Empty>
    if (type === "markdown") {
      return (
        <MarkdownValue>
          <ReactMarkdown>{value}</ReactMarkdown>
        </MarkdownValue>
      )
    }
    return <Value $multiline={type === "textarea"}>{value}</Value>
  }

  return (
    <Wrapper>
      <LabelRow>
        <Label>{label}</Label>
        {canEdit && !isEditing && (
          <EditButton
            type="button"
            onClick={startEditing}
            title={`Edit ${label}`}
          >
            <PencilIcon />
            <span>Edit</span>
          </EditButton>
        )}
      </LabelRow>

      {isEditing ? (
        <EditArea>
          {type === "text" && (
            <TextInput
              autoFocus
              value={draft}
              placeholder={placeholder}
              disabled={isSaving}
              onChange={e => setDraft(e.target.value)}
              onKeyDown={handleKeyDown}
            />
          )}

          {type === "textarea" && (
            <TextArea
              autoFocus
              value={draft}
              placeholder={placeholder}
              disabled={isSaving}
              rows={4}
              onChange={e => setDraft(e.target.value)}
              onKeyDown={handleKeyDown}
            />
          )}

          {type === "markdown" && (
            <MdEditorWrapper onKeyDown={handleKeyDown}>
              <MdEditor
                value={draft}
                placeholder={placeholder}
                style={{ minHeight: "220px" }}
                onChange={({ text }) => setDraft(text)}
                renderHTML={text => mdParser.render(text)}
              />
            </MdEditorWrapper>
          )}

          {type === "markdown" && (
            <Hint>⌘/Ctrl+Enter to save · Esc to cancel</Hint>
          )}

          <Actions>
            <SaveBtn type="button" onClick={handleSave} disabled={isSaving}>
              {isSaving ? "Saving…" : "Save"}
            </SaveBtn>
            <CancelBtn
              type="button"
              onClick={cancelEditing}
              disabled={isSaving}
            >
              Cancel
            </CancelBtn>
          </Actions>
        </EditArea>
      ) : (
        renderReadValue()
      )}
    </Wrapper>
  )
}

/* ── Styled ── */

const Wrapper = styled.div``

const LabelRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
`

const Label = styled.div`
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${colors.gray6};
`

const EditButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 2px 7px;
  border: 1px solid ${colors.gray2};
  border-radius: 12px;
  background: ${colors.white};
  color: ${colors.gray6};
  cursor: pointer;
  /* Always faintly visible so users can discover it, fully visible on hover. */
  opacity: 0.55;
  transition:
    opacity 0.15s ease,
    color 0.15s ease,
    border-color 0.15s ease,
    background 0.15s ease;

  &:hover {
    color: ${colors.gray3};
    border-color: ${colors.caribbean};
    background: ${colors.gray5};
  }

  ${Wrapper}:hover & {
    opacity: 1;
  }

  span {
    font-size: 11px;
    font-weight: 600;
    line-height: 1;
  }
`

const Value = styled.div<{ $multiline?: boolean }>`
  font-size: 14px;
  color: ${colors.gray3};
  line-height: 1.5;
  ${({ $multiline }) =>
    $multiline && "white-space: pre-wrap; word-break: break-word;"}
`

const Empty = styled.div`
  font-size: 14px;
  color: ${colors.gray4};
`

const MarkdownValue = styled.div`
  font-size: 14px;
  color: ${colors.gray3};
  line-height: 1.7;
  word-break: break-word;

  p {
    margin: 0 0 10px;
  }
  p:last-child {
    margin-bottom: 0;
  }
  ul,
  ol {
    margin: 0 0 10px;
    padding-left: 20px;
  }
  a {
    color: ${colors.blue1};
  }
`

const EditArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const sharedInput = `
  width: 100%;
  font-size: 14px;
  color: ${colors.gray3};
  padding: 8px 10px;
  border: 1px solid ${colors.gray2};
  border-radius: 6px;
  outline: none;
  font-family: inherit;
  &:focus {
    border-color: ${colors.caribbean};
  }
`

const TextInput = styled.input`
  ${sharedInput}
`

const TextArea = styled.textarea`
  ${sharedInput}
  resize: vertical;
  line-height: 1.5;
`

const MdEditorWrapper = styled.div`
  .rc-md-editor {
    border: 1px solid ${colors.gray2};
    border-radius: 6px;
  }
`

const Hint = styled.span`
  font-size: 11px;
  color: ${colors.gray4};
`

const Actions = styled.div`
  display: flex;
  gap: 8px;
`

const SaveBtn = styled.button`
  font-size: 13px;
  font-weight: 600;
  padding: 6px 14px;
  border: none;
  border-radius: 6px;
  background: ${colors.caribbean};
  color: ${colors.gray3};
  cursor: pointer;
  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
`

const CancelBtn = styled.button`
  font-size: 13px;
  font-weight: 600;
  padding: 6px 14px;
  border: 1px solid ${colors.gray2};
  border-radius: 6px;
  background: ${colors.white};
  color: ${colors.gray6};
  cursor: pointer;
  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
`

export default EditableField
